/**
 * Geometry of a list menu beside the pointer, in window pixels with y
 * pointing down. The list is a column of equal rows with a gap between
 * them, inside a bordered box that scrolls when it holds more rows than it
 * shows. The sizes are those of the app's menus: the paper's padding, the
 * rows' height and the gap between them.
 */
import type { Point } from '~/components/toolbar/pointerMenu/PointerMenu';

export const LIST_ROW_PX = 28;
export const LIST_GAP_PX = 4;
/** From the top of one row to the top of the next. */
export const LIST_ROW_PITCH_PX = LIST_ROW_PX + LIST_GAP_PX;
/** The room between the border and the rows, on every side. */
export const LIST_PAD_PX = 4;
export const LIST_BORDER_PX = 1;
export const LIST_WIDTH_PX = 240;
/** The most rows the box shows; more scroll. */
export const LIST_MAX_ROWS = 12;
/** The room at the right edge a scrollbar takes; no row is picked there. */
export const LIST_SCROLLBAR_PX = 12;
/** The gap between the pointer and the side of the list. */
const POINTER_GAP_PX = 12;
/** The least room kept between the list and the window edge. */
const WINDOW_MARGIN_PX = 8;

/** The height of `rows` rows with the padding around them. */
function contentHeight(rows: number): number {
    return (
        2 * LIST_PAD_PX +
        rows * LIST_ROW_PX +
        Math.max(0, rows - 1) * LIST_GAP_PX
    );
}

/** The height of the box for `rows` rows, border included. */
export function listHeight(rows: number): number {
    return contentHeight(Math.min(rows, LIST_MAX_ROWS)) + 2 * LIST_BORDER_PX;
}

/** The top of row `index`, measured from the top of the scrolled content. */
function rowTop(index: number): number {
    return LIST_PAD_PX + index * LIST_ROW_PITCH_PX;
}

export interface ListLayout {
    /** The box's top-left corner. */
    topLeft: Point;
    scrollTop: number;
}

/**
 * Where a list of `rows` rows goes for a pointer at `pointer`: beside it,
 * on the right where it fits, else on the left, with row `current` scrolled
 * into view and level with the pointer as far as the window allows. With no
 * current row, the first row is level with the pointer.
 */
export function layoutList(
    pointer: Point,
    rows: number,
    current: number | null,
    windowWidth: number,
    windowHeight: number
): ListLayout {
    const height = listHeight(rows);
    const viewport = height - 2 * LIST_BORDER_PX;
    const maxScroll = Math.max(0, contentHeight(rows) - viewport);
    const top = rowTop(current ?? 0);
    const scrollTop = Math.min(
        Math.max(0, top + LIST_ROW_PX / 2 - viewport / 2),
        maxScroll
    );
    const right = pointer.x + POINTER_GAP_PX;
    const x =
        right + LIST_WIDTH_PX <= windowWidth - WINDOW_MARGIN_PX
            ? right
            : pointer.x - POINTER_GAP_PX - LIST_WIDTH_PX;
    const y = pointer.y - LIST_BORDER_PX - (top - scrollTop) - LIST_ROW_PX / 2;
    const clamp = (value: number, size: number, extent: number) =>
        Math.min(
            Math.max(value, WINDOW_MARGIN_PX),
            Math.max(WINDOW_MARGIN_PX, size - extent - WINDOW_MARGIN_PX)
        );
    return {
        topLeft: {
            x: clamp(x, windowWidth, LIST_WIDTH_PX),
            y: clamp(y, windowHeight, height),
        },
        scrollTop,
    };
}

/**
 * Whether `at` is in the band at the right edge of a list of `rows` rows
 * drawn at `topLeft` that its scrollbar takes. A list that does not scroll
 * has none.
 */
export function overScrollbar(
    at: Point,
    topLeft: Point,
    rows: number
): boolean {
    return (
        rows > LIST_MAX_ROWS &&
        at.x - topLeft.x >= LIST_WIDTH_PX - LIST_BORDER_PX - LIST_SCROLLBAR_PX
    );
}

/**
 * The row at `at` in a list of `rows` rows drawn at `topLeft` and scrolled
 * by `scrollTop`, or null outside the box, over the scrollbar, and above the
 * first row or below the last. Each gap is split between the rows beside it.
 */
export function rowAt(
    at: Point,
    topLeft: Point,
    rows: number,
    scrollTop: number
): number | null {
    const x = at.x - topLeft.x;
    const y = at.y - topLeft.y;
    if (
        x < 0 ||
        x >= LIST_WIDTH_PX ||
        y < 0 ||
        y >= listHeight(rows) ||
        overScrollbar(at, topLeft, rows)
    ) {
        return null;
    }
    const inContent = y - LIST_BORDER_PX + scrollTop;
    const index = Math.floor(
        (inContent - LIST_PAD_PX + LIST_GAP_PX / 2) / LIST_ROW_PITCH_PX
    );
    return index >= 0 && index < rows ? index : null;
}
